import type { ReactNode } from 'react';

import { Skeleton } from '~/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import type { StatusTone } from '../../utils/run-status';
import { ContentStatusChip } from '../content-status-chip';
import { PageHeader } from '../ui-kit/page-header';

/** Catalog runs live on Runs — the back link opens the whole list (no type filter). */
const CATALOG_BACK = { to: '/content/runs', label: 'Runs' };

/** One context chip under the facts line ("Instructions · Lily default · v1"). */
export interface CatalogChip {
  label: string;
  value: string;
  /** Wraps the chip in a link (Types / Fields → Set up, Instructions → AI instructions). */
  link?: ((content: ReactNode) => ReactNode) | undefined;
  /** Hover text with the full list (every instructions version, the types left out). */
  tooltip?: string | undefined;
}

/** The chip's look; a linked chip gets the hover and pointer of a link. */
const CHIP_CLASS =
  'border-border text-muted-foreground inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs';

/** One chip: plain, linked, and/or with a tooltip naming everything it sums up. */
function HeaderChip({ chip }: { chip: CatalogChip }) {
  const content = (
    <>
      {chip.label}
      <span className="text-foreground font-medium">{chip.value}</span>
    </>
  );
  const body = chip.link ? chip.link(content) : <span className={CHIP_CLASS}>{content}</span>;
  if (!chip.tooltip) return body;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{body}</TooltipTrigger>
      <TooltipContent className="max-w-sm">{chip.tooltip}</TooltipContent>
    </Tooltip>
  );
}

/** Class for a linked chip — the page's `link` puts it on its `Link`. */
export const LINKED_CHIP_CLASS = cn(
  CHIP_CLASS,
  'hover:border-primary hover:text-primary cursor-pointer transition-colors',
);

/** A chip rendered as a `Button` (opens a sheet): the linked look, without the button's own sizing. */
export const CHIP_BUTTON_CLASS = cn(
  LINKED_CHIP_CLASS,
  'h-auto font-normal hover:bg-transparent focus-visible:ring-2',
);

/**
 * The catalog job header — back to Runs, "<setup title> · Catalog run" with the job's status pill,
 * one facts line (only what the title doesn't say), the context chips (fields, instructions) and
 * the step's own actions.
 */
export function CatalogJobHeader({
  title,
  status,
  facts,
  chips = [],
  actions,
  inline = false,
  kindSuffix = true,
}: {
  title: ReactNode;
  status: { label: string; tone: StatusTone; pulse?: boolean; explanation?: string } | null;
  facts: ReactNode[];
  chips?: CatalogChip[];
  actions?: ReactNode;
  /** Facts and chips on one wrapping line (Set up, to match QC Set up's header). */
  inline?: boolean;
  /** Appends " · Catalog run" to the title; off when the title already names it (Set up). */
  kindSuffix?: boolean;
}) {
  return (
    <PageHeader
      back={CATALOG_BACK}
      title={
        <>
          {title}
          {kindSuffix && <span className="text-muted-foreground font-normal"> · Catalog run</span>}
        </>
      }
      badge={
        status && (
          <ContentStatusChip
            status={status.label}
            label={status.label}
            tone={status.tone}
            pulse={status.pulse ?? false}
            {...(status.explanation && { explanation: status.explanation })}
          />
        )
      }
      meta={
        inline ? (
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {facts.map((fact, index) => (
              <span key={index} className="contents">
                {index > 0 && <span aria-hidden="true">·</span>}
                <span>{fact}</span>
              </span>
            ))}
            {chips.map((chip) => (
              <HeaderChip key={chip.label} chip={chip} />
            ))}
          </span>
        ) : (
          <span className="flex flex-col gap-2">
            {facts.length > 0 && (
              <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
                {facts.map((fact, index) => (
                  <span key={index}>{fact}</span>
                ))}
              </span>
            )}
            {chips.length > 0 && (
              <span className="flex flex-wrap gap-2">
                {chips.map((chip) => (
                  <HeaderChip key={chip.label} chip={chip} />
                ))}
              </span>
            )}
          </span>
        )
      }
      actions={actions}
    />
  );
}

/** The header while the job is read: the real back link, then title and facts as bars. */
export function CatalogJobHeaderSkeleton() {
  return (
    <PageHeader
      back={CATALOG_BACK}
      title={
        <>
          <span className="sr-only">Loading catalog run</span>
          <Skeleton aria-hidden="true" className="inline-block h-7 w-80 max-w-full align-middle" />
        </>
      }
      meta={<Skeleton aria-hidden="true" className="h-4 w-96 max-w-full" />}
    />
  );
}
